import { cn } from "@/common/utils";
import { cva, VariantProps } from "class-variance-authority";
import {
  createElement,
  ReactNode,
  ElementType as ReactElementType,
} from "react";

const typographyVariants = cva("", {
  variants: {
    family: {
      kanit: "font-kanit",
      inter: "font-inter",
    },
    size: {
      "12px": "text-[12px]",
      "13px": "text-[13px]",
      "14px": "text-[14px]",
      "15px": "text-[15px]",
      base: "text-base",
      "21px": "text-[21px]",
    },
    weight: {
      light: "font-light", // 300
      normal: "font-normal", // 400
      medium: "font-medium", // 500
      semibold: "font-semibold", // 600,
    },
    leading: {
      "100%": "leading-none", //for custom leading use className prop
    },
    case: {
      lowercase: "lowercase",
      uppercase: "uppercase",
      capitalize: "capitalize",
      normal: "normal-case",
    },
    textAlign: {
      start: "text-start",
      center: "text-center",
      end: "text-end",
    },
    color: {
      main: "text-black",
      gray: "text-gray-dim",
      black60: "text-blackTransparent-60",
      black80: "text-blackTransparent-80",
      black90: "text-blackTransparent-90",
      primary: "text-primary",
      white: "text-white",
      error: "text-red-main",
      yellow: "text-yellow-secondary",
      green: "text-success-dark", // for custom color use className prop
    },
  },
  defaultVariants: {
    case: "capitalize",
    color: "main",
    family: "kanit",
    leading: "100%",
    size: "14px",
    textAlign: "start",
    weight: "normal",
  },
});
type CustomTypographyProps<T extends ReactElementType> = VariantProps<
  typeof typographyVariants
> &
  Omit<
    React.ComponentPropsWithoutRef<T>,
    keyof VariantProps<typeof typographyVariants>
  > & {
    as?: T;
    children?: ReactNode;
  };

export default function CustomTypography<T extends ReactElementType = "p">({
  as,
  children,
  className,
  case: textCase,
  family,
  leading,
  size,
  textAlign,
  weight,
  color,
  ...props
}: CustomTypographyProps<T>) {
  const Component = as || "p";
  return createElement(
    Component,
    {
      ...props,
      className: cn(
        typographyVariants({
          case: textCase,
          color,
          family,
          leading,
          size,
          textAlign,
          weight,
        }),
        className
      ),
    },
    children
  );
}
